import { addDays, format } from "date-fns";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { APIs } from "../../apis";
import { OrderItemPlanningTab, type OrderItem } from "../../apis/order";
import type { DatePickerRangeValue } from "../../components";
import {
  SortOrder,
  type TableColumn,
  type TableSorter,
} from "../../components/Table";
import { useApi, useFilters, useNavigation } from "../../hooks";
import { Constants, Tools } from "../../utils";
import { getManageBookingsColumns } from "./columns";

const DEFAULT_PAGE_SIZE = 10;
const ORDER_BY_OPTIONS = [
  "",
  ...getManageBookingsColumns({ t: (key) => key }).flatMap((c) =>
    c.sorter && c.key ? [String(c.key), `-${c.key}`] : [],
  ),
];

export function useViewModel() {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const { fetch, data, loading } =
    useApi<Awaited<ReturnType<typeof APIs.order.orderItems>>>();

  const defaultDateRange = React.useMemo(() => {
    const today = new Date();
    return {
      from: format(today, "yyyy-MM-dd"),
      to: format(addDays(today, 30), "yyyy-MM-dd"),
    };
  }, []);

  const skeletonOnNextLoad = React.useRef(true);

  const { filters, setFilter, setSearchParams, isValid } = useFilters({
    defaultValues: {
      searchString: "",
      startDate: defaultDateRange.from,
      endDate: defaultDateRange.to,
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      orderBy: "-serviceDateTime",
    },
    validate: (params) => ({
      orderBy: !Tools.isOneOf(params.orderBy, ORDER_BY_OPTIONS),
    }),
    modifyFilter: (name, value) => {
      skeletonOnNextLoad.current = name !== "orderBy";
      return {
        ...(name === "dateRange" && typeof value === "object" && value
          ? {
              startDate: (value as DatePickerRangeValue).from,
              endDate: (value as DatePickerRangeValue).to,
            }
          : { [name]: value as string | number }),
        page: 1,
      };
    },
  });

  const { results: dataSource = [], count: totalItems = 0 } =
    data?.orders ?? {};

  const showSkeleton = loading && skeletonOnNextLoad.current;
  const showGlobalEmpty = !loading && !totalItems && filters.page === 1;

  const openBookingDetail = React.useCallback(
    (item: OrderItem) =>
      navigate(
        `${Constants.RoutePath.ManageBookings}/${item.orderId ?? item.order?.id}`,
      ),
    [navigate],
  );

  const columns = React.useMemo(() => getManageBookingsColumns({ t }), [t]);

  const tableSorter = React.useMemo((): TableSorter | undefined => {
    if (!filters.orderBy) return undefined;
    const order = filters.orderBy.startsWith("-")
      ? SortOrder.descend
      : SortOrder.ascend;
    return {
      field: filters.orderBy.slice(order === SortOrder.descend ? 1 : 0),
      order,
    };
  }, [filters.orderBy]);

  const getData = React.useCallback(async () => {
    if (!isValid) return;
    try {
      await fetch(() =>
        APIs.order.orderItems({
          page: filters.page,
          pageSize: filters.pageSize,
          orderBy: filters.orderBy || undefined,
          planningTab: OrderItemPlanningTab.all,
          searchString: filters.searchString,
          startDate: filters.startDate,
          endDate: filters.endDate,
        }),
      );
    } catch {
    } finally {
      skeletonOnNextLoad.current = false;
    }
  }, [
    fetch,
    filters.orderBy,
    filters.startDate,
    filters.endDate,
    filters.page,
    filters.pageSize,
    filters.searchString,
    isValid,
  ]);

  React.useEffect(() => {
    void getData();
  }, [getData]);

  const onTableChange = React.useCallback(
    ({ sorter }: { sorter?: TableSorter }) => {
      setFilter(
        "orderBy",
        sorter
          ? `${sorter.order === SortOrder.descend ? "-" : ""}${sorter.field}`
          : "",
      );
    },
    [setFilter],
  );

  return {
    t,
    columns: columns as TableColumn<OrderItem>[],
    tableSorter,
    dataSource,
    totalItems,
    loading,
    showSkeleton,
    showGlobalEmpty,
    page: filters.page,
    pageSize: filters.pageSize,
    searchString: filters.searchString,
    dateRange: {
      from: filters.startDate,
      to: filters.endDate,
    },
    setFilter,
    onPageChange: (page: number) => setSearchParams({ page }),
    onPageSizeChange: (pageSize: number) =>
      setSearchParams({ pageSize, page: 1 }),
    onRowClick: openBookingDetail,
    onTableChange,
  };
}

export type ManageBookingsViewModel = ReturnType<typeof useViewModel>;
